<script setup lang="ts">
import { computed } from "vue";

import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import LeafIcon from "@/assets/icons/leaves/leaf.svg?component";
import ThreeLeavesIcon from "@/assets/icons/leaves/leaves-three.svg?component";
import TwoLeavesIcon from "@/assets/icons/leaves/leaves-two.svg?component";
import {
  DEFAULT_PODIUM_COLORS,
  MONOSITE_PODIUM_COLORS,
  IPodiumColors,
  IPodiumItem,
} from "@/components/data/dashboard/insights-podium/insights-podium.types";
import { cn } from "@/utils/libs/tailwind-utils";
import { isDefined } from "@/utils/object";

interface IProps {
  items?: IPodiumItem[];
  type: EDashboardType;
}

const props = defineProps<IProps>();

const colors = computed<IPodiumColors>(() => {
  return props.type === EDashboardType.MONOSITE ? MONOSITE_PODIUM_COLORS : DEFAULT_PODIUM_COLORS;
});

const filteredItems = computed(() => {
  return props.items?.filter(isDefined) ?? [];
});

const first = computed(() => {
  return filteredItems.value[0];
});
const second = computed(() => {
  return filteredItems.value[1];
});
const third = computed(() => {
  return filteredItems.value[2];
});
</script>

<template>
  <div class="flex justify-center h-full min-h-[200px] insights-podium w-full">
    <div class="flex justify-center w-full gap-2 max-w-[350px]">
      <div
        v-if="second"
        class="insights-podium__step"
      >
        <div class="h-full w-full relative flex justify-center">
          <div
            v-tooltip.top="second?.tooltip"
            :class="cn('insights-podium__step__block h-3/5', colors.second.fromColor, colors.second.toColor)"
          >
            <div class="flex justify-center w-full items-center pt-2">
              <i :class="cn('insights-podium__step__block__number', colors.second.fromColor, colors.second.toColor)"
                >2</i
              >
            </div>
          </div>
          <TwoLeavesIcon class="absolute top-[40px]" />
        </div>
        <div class="flex justify-center w-full">
          <small
            class="whitespace-nowrap overflow-hidden text-ellipsis"
            :title="second?.name"
          >
            {{ second?.name ?? "—" }}
          </small>
        </div>
      </div>
      <div class="insights-podium__step">
        <div class="h-full relative flex justify-center">
          <div
            v-tooltip.top="first?.tooltip"
            :class="cn('insights-podium__step__block h-5/6', colors.first.fromColor, colors.first.toColor)"
          >
            <div class="flex justify-center w-full items-center pt-2">
              <i :class="cn('insights-podium__step__block__number', colors.first.fromColor, colors.first.toColor)">1</i>
            </div>
          </div>
          <ThreeLeavesIcon />
        </div>
        <div class="flex justify-center w-full">
          <small
            class="whitespace-nowrap overflow-hidden text-ellipsis"
            :title="first?.name"
          >
            {{ first?.name ?? "—" }}
          </small>
        </div>
      </div>
      <div
        v-if="third"
        class="insights-podium__step"
      >
        <div class="h-full relative flex justify-center">
          <div
            v-tooltip.top="third?.tooltip"
            :class="cn('insights-podium__step__block h-1/3', colors.third.fromColor, colors.third.toColor)"
          >
            <div class="flex justify-center w-full items-center pt-2">
              <i :class="cn('insights-podium__step__block__number', colors.third.fromColor, colors.third.toColor)">3</i>
            </div>
          </div>
          <LeafIcon class="relative top-[90px]" />
        </div>
        <div class="flex justify-center w-full">
          <small
            class="whitespace-nowrap overflow-hidden text-ellipsis"
            :title="third?.name"
          >
            {{ third?.name ?? "—" }}
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.insights-podium {
  &__step {
    @apply flex flex-col h-full gap-2 w-[100px] shrink-0;

    &__block {
      @apply bg-gradient-to-b rounded-md absolute bottom-0 left-0 right-0;

      &__number {
        @apply rounded-full bg-gradient-to-t w-7 flex justify-center not-italic text-white text-xl;
      }
    }
  }
}
</style>
